/**
 * Bookmarks Widget Component
 * Top-level widget for Basecamp bookmarks, separate from "My Stuff"
 */

import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ChevronDown,
  ChevronRight,
  Bookmark,
  Loader2,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { StorageKeys, StorageSchemas } from '@/lib/storage';
import { basecampApi } from '@/lib/basecamp-api';
import { useBasecampAuth } from '@/hooks/use-basecamp';
import { cn } from '@/lib/utils';
import { BasecampDueBadge } from './basecamp-due-badge';
import type { BasecampAssignment } from '@/types/basecamp';

export function useBookmarksData(organizationId?: string) {
  const { authStatus } = useBasecampAuth(organizationId);
  const [bookmarks, setBookmarks] = useState<BasecampAssignment[]>([]);
  const [bookmarksLoading, setBookmarksLoading] = useState(false);
  const [bookmarksError, setBookmarksError] = useState<string | null>(null);
  const [newlyAddedBookmarkIds, setNewlyAddedBookmarkIds] = useState<Set<string>>(new Set());

  const loadBookmarks = useCallback(async (forceSync = false) => {
    if (authStatus && !authStatus.authenticated) {
      setBookmarksError('Not authenticated');
      setBookmarksLoading(false);
      return;
    }

    setBookmarksLoading(true);
    setBookmarksError(null);
    try {
      const data = await basecampApi.getMyBookmarks(forceSync, organizationId);
      const transformedBookmarks: BasecampAssignment[] = (data.items || []).map((item: BasecampAssignment & {
        project_id?: string;
        project_name?: string;
        bucket_id?: string;
        basecamp_todo_id?: string;
      }) => ({
        ...item,
        project: item.project || {
          id: item.project_id?.toString() || item.bucket_id?.toString() || '',
          name: item.project_name || item._project_name || 'Unknown Project',
        },
        _project_name: item._project_name || item.project_name || item.project?.name || 'Unknown Project',
        id: item.id?.toString() || item.basecamp_todo_id?.toString() || '',
      }));
      setBookmarks(transformedBookmarks);
    } catch (err) {
      console.error('[BookmarksWidget] Error loading bookmarks:', err);
      setBookmarksError(err instanceof Error ? err.message : 'Failed to load bookmarks');
    } finally {
      setBookmarksLoading(false);
    }
  }, [authStatus, organizationId]);

  const optimisticallyUpdateBookmark = useCallback((
    todoId: string,
    projectId: string,
    projectName: string,
    title: string,
    added: boolean,
  ) => {
    const todoIdStr = todoId.toString();

    if (added) {
      const optimisticBookmark: BasecampAssignment = {
        id: todoIdStr,
        title,
        url: '',
        completed: false,
        project: { id: projectId, name: projectName },
        assignee: { id: '', name: '' },
        project_id: projectId,
        project_name: projectName,
        bucket_id: projectId,
      };

      setBookmarks((prev) => {
        if (prev.some((b) => b.id.toString() === todoIdStr)) return prev;
        return [optimisticBookmark, ...prev];
      });

      setNewlyAddedBookmarkIds((prev) => new Set([...prev, todoIdStr]));
      setTimeout(() => {
        setNewlyAddedBookmarkIds((prev) => {
          const next = new Set(prev);
          next.delete(todoIdStr);
          return next;
        });
      }, 2000);
    } else {
      setBookmarks((prev) => prev.filter((b) => b.id.toString() !== todoIdStr));
    }
  }, []);

  useEffect(() => {
    if (authStatus === null) return;
    if (authStatus.authenticated) {
      loadBookmarks();
    } else {
      setBookmarksError('Not authenticated');
      setBookmarksLoading(false);
    }
  }, [authStatus, organizationId, loadBookmarks]);

  useEffect(() => {
    const onReload = () => {
      if (authStatus?.authenticated) loadBookmarks(true);
    };
    window.addEventListener('basecamp-bookmarks-reload', onReload);
    return () => window.removeEventListener('basecamp-bookmarks-reload', onReload);
  }, [authStatus?.authenticated, loadBookmarks]);

  useEffect(() => {
    const handleBookmarkChange = (event: CustomEvent) => {
      const {
        todoId,
        projectId,
        projectName,
        title,
        added,
        optimistic,
        synced,
      } = event.detail || {};

      if (optimistic && todoId && projectId) {
        optimisticallyUpdateBookmark(
          todoId.toString(),
          projectId.toString(),
          projectName || 'Unknown Project',
          title || 'Untitled Todo',
          added,
        );
      } else if (synced) {
        loadBookmarks(false);
      } else {
        if (added && todoId) {
          setNewlyAddedBookmarkIds((prev) => new Set([...prev, todoId.toString()]));
          setTimeout(() => {
            setNewlyAddedBookmarkIds((prev) => {
              const next = new Set(prev);
              next.delete(todoId.toString());
              return next;
            });
          }, 2000);
        }
        loadBookmarks();
      }
    };

    window.addEventListener('basecamp-bookmark-changed', handleBookmarkChange as EventListener);
    return () => {
      window.removeEventListener('basecamp-bookmark-changed', handleBookmarkChange as EventListener);
    };
  }, [loadBookmarks, optimisticallyUpdateBookmark]);

  return {
    authStatus,
    bookmarks,
    bookmarksLoading,
    bookmarksError,
    newlyAddedBookmarkIds,
    loadBookmarks,
  };
}

interface BookmarksWidgetProps {
  onAssignmentClick?: (assignment: BasecampAssignment) => void;
  /** Workspace organization UUID (scopes bookmarks API when set). */
  organizationId?: string;
}

export function BookmarksWidget({ onAssignmentClick, organizationId }: BookmarksWidgetProps) {
  const {
    authStatus,
    bookmarks,
    bookmarksLoading,
    bookmarksError,
    newlyAddedBookmarkIds,
    loadBookmarks,
  } = useBookmarksData(organizationId);
  const [isExpanded, setIsExpanded] = usePersistedState({
    key: StorageKeys.BASECAMP_BOOKMARKS_EXPANDED,
    defaultValue: true,
    schema: StorageSchemas.boolean,
  });

  const handleAssignmentClick = (assignment: BasecampAssignment) => {
    const tabId = `basecamp-todo-${assignment.id}`;
    const projectId = assignment.project?.id || assignment.project_id || assignment.bucket_id;
    const projectName = assignment.project?.name || assignment.project_name || assignment._project_name || 'Unknown Project';

    if (!projectId) {
      console.error('[BookmarksWidget] Assignment missing project ID:', assignment);
      return;
    }

    window.dispatchEvent(
      new CustomEvent('integration-view-change', {
        detail: {
          tabId,
          title: assignment.title,
          type: 'integration-basecamp-todo',
          integrationData: {
            integrationId: 'basecamp',
            projectId: projectId.toString(),
            todoId: assignment.id.toString(),
            projectName,
            ...(organizationId ? { organizationId } : {}),
          },
        },
      }),
    );

    onAssignmentClick?.(assignment);
  };

  useEffect(() => {
    if (isExpanded && authStatus?.authenticated) {
      loadBookmarks();
    }
  }, [isExpanded, authStatus?.authenticated, organizationId, loadBookmarks]);

  return (
    <div className="flex-shrink-0 flex flex-col h-full min-h-0 overflow-hidden">
      <div className="px-3 py-2 flex items-center justify-between flex-shrink-0">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex-1 flex items-center gap-2 hover:bg-accent/50 rounded px-2 py-1 -mx-2 -my-1"
        >
          {isExpanded ? (
            <ChevronDown className="h-3 w-3 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3 w-3 text-muted-foreground" />
          )}
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
            <Bookmark className="h-3 w-3" />
            Bookmarks
          </h3>
          {bookmarks.length > 0 && (
            <Badge variant="secondary" className="text-xs">
              {bookmarks.length}
            </Badge>
          )}
        </button>

        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={() => loadBookmarks(true)}
          disabled={bookmarksLoading}
          title="Re-verify bookmarks with Basecamp (refresh todo data)"
        >
          <RefreshCw className={cn('h-3 w-3', bookmarksLoading && 'animate-spin')} />
        </Button>
      </div>

      {isExpanded && (
        <div className="flex-1 overflow-y-auto min-h-0 px-2 pb-2 space-y-1 border-t border-border">
          {bookmarksLoading && (
            <div className="flex items-center justify-center py-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin mr-2" />
              Loading bookmarks...
            </div>
          )}

          {bookmarksError && (
            <div className="flex flex-col items-center gap-1 py-2">
              <AlertCircle className="h-3 w-3 text-red-500" />
              <div className="text-[10px] text-red-500 text-center">{bookmarksError}</div>
            </div>
          )}

          {!bookmarksLoading && !bookmarksError && bookmarks.length === 0 && (
            <div className="flex flex-col items-center gap-1 py-2">
              <div className="text-[10px] text-muted-foreground text-center">
                No bookmarked items
              </div>
              <div className="text-[9px] text-muted-foreground/70 text-center px-2 max-w-[220px]">
                Bookmark from a todo here, or tap refresh to discover bookmarks you set on basecamp.com for todos assigned to you.
              </div>
            </div>
          )}

          {!bookmarksLoading && !bookmarksError && bookmarks.length > 0 && (
            <div className="space-y-1">
              {bookmarks.map((bookmark) => (
                <AssignmentCard
                  key={bookmark.id}
                  assignment={bookmark}
                  onClick={() => handleAssignmentClick(bookmark)}
                  isNewlyAdded={newlyAddedBookmarkIds.has(bookmark.id.toString())}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AssignmentCard({
  assignment,
  onClick,
  isNewlyAdded = false,
}: {
  assignment: BasecampAssignment;
  onClick: () => void;
  isNewlyAdded?: boolean;
}) {
  const isOverdue = assignment._is_overdue;

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left p-2 rounded-md hover:bg-accent transition-all duration-300 flex flex-col gap-1',
        isOverdue && 'bg-red-500/5 border border-red-500/20',
        isNewlyAdded && [
          'ring-2 ring-blue-500/50 ring-offset-1 ring-offset-background',
          'bg-blue-500/10',
        ],
      )}
      style={isNewlyAdded ? {
        animation: 'slideIn 0.5s ease-out forwards, glow 1.5s ease-out',
      } : undefined}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs font-medium truncate flex-1">
          {assignment.title}
        </span>
        {assignment.due_on ? (
          <BasecampDueBadge
            dueOn={assignment.due_on}
            isOverdue={isOverdue}
            className="px-1 py-0 h-4"
          />
        ) : null}
      </div>

      <div className="text-[10px] text-muted-foreground truncate">
        {assignment._project_name}
      </div>
    </button>
  );
}
